import styled from '@emotion/styled';
import { ReactNode, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { privacyPolicyURL } from '../../common/urls';
import {
  isSupportedLocale,
  t,
  useLocale,
} from '../../localization/LocaleContext';
import {
  useChatbotAccessRightRequest,
  useModifyChatbotAccessRightRequest,
} from '../../remoteData/unauthenticated';
import LoadingScreen from '../LoadingScreen';
import { ButtonPrimary } from '../shared/Buttons';

const Container = styled.div`
  color: ${({ theme }) => theme.colors.blueDark70};
  max-width: 560px;
`;

const Title = styled.h1`
  ${({ theme }) => theme.textStyles.titleL};
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.blueDark100};
  margin-bottom: 21px;

  ${({ theme }) => theme.mediaQuery.md} {
    margin-bottom: ${({ theme }) => theme.spacers.m};
  }
`;

const Paragraph = styled.p`
  ${({ theme }) => theme.textStyles.labelL};

  a {
    color: ${({ theme }) => theme.colors.blueDark100};
    text-decoration: underline;
  }
`;

const ErrorComponent = styled.p`
  ${({ theme }) => theme.textStyles.paragraphS};
  color: ${({ theme }) => theme.colors.blueDark50};
  margin-top: ${({ theme }) => theme.spacers.xl};
`;

const ApproveButton = styled(ButtonPrimary)`
  margin-top: ${({ theme }) => theme.spacers.l};
  margin-bottom: ${({ theme }) => theme.spacers.l};
  width: 100%;

  ${({ theme }) => theme.mediaQuery.md} {
    min-width: 172px;
    width: auto;
  }
`;

export default function ChatbotSessionRequest() {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale');
  const token = searchParams.get('token') ?? undefined;
  const { selectedLocale, changeLocale } = useLocale();

  useAnonymousAnalyticsTrackPage({
    category: 'Public',
    name: 'Chatbot Session Request',
  });

  /**
   * When we have new token which is valid:
   * chatbotAccessRightsData = { tokenExpired: false, state: 'REQUESTED' }
   * modifyRightsData = undefined
   *
   * After we have requested and granted access:
   * chatbotAccessRightsData = { tokenExpired: false, state: 'REQUESTED' }
   * modifyRightsData = { state: 'GRANTED' }
   */
  const { data: chatbotAccessRightsData, error: chatbotAccessRightsDataError } =
    useChatbotAccessRightRequest(token);
  const {
    mutate: modifyRights,
    error: modifyRightsError,
    data: modifyRightsData,
  } = useModifyChatbotAccessRightRequest();

  useLayoutEffect(() => {
    if (locale && !Array.isArray(locale) && isSupportedLocale(locale)) {
      changeLocale(locale);
    }
  }, [changeLocale, locale]);

  if (!token || chatbotAccessRightsDataError || modifyRightsError) {
    return (
      <Container>
        <Title>{t('general_invalid_link_title')}</Title>
        <Paragraph>{t('general_invalid_link_text')}</Paragraph>
        {chatbotAccessRightsDataError && (
          <ErrorComponent>
            {chatbotAccessRightsDataError.message}
          </ErrorComponent>
        )}
        {modifyRightsError && (
          <ErrorComponent>{modifyRightsError.message}</ErrorComponent>
        )}
      </Container>
    );
  }

  // Show loading screen until we have validated the token in the URL
  if (!chatbotAccessRightsData) {
    return <LoadingScreen />;
  }

  if (
    chatbotAccessRightsData.tokenExpired ||
    chatbotAccessRightsData.state === 'REVOKED' ||
    modifyRightsData?.state === 'REVOKED'
  ) {
    return (
      <Container>
        <Title>{t('chatbot_access_request_link_expired_title')}</Title>
        <Paragraph>{t('chatbot_access_request_link_expired_text')}</Paragraph>
      </Container>
    );
  }

  if (
    modifyRightsData?.state === 'GRANTED' ||
    chatbotAccessRightsData.state === 'GRANTED'
  ) {
    return (
      <Container>
        <Title>{t('chatbot_access_request_access_granted_title')}</Title>
        <Paragraph>{t('chatbot_access_request_access_granted_text')}</Paragraph>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('chatbot_access_request_title')}</Title>
      <Paragraph>{t('chatbot_access_request_paragraph')}</Paragraph>
      <ApproveButton
        size="xsmall"
        rounded
        type="submit"
        onClick={() => {
          modifyRights({
            token,
            acceptAccess: true,
          });
        }}
      >
        {t('chatbot_access_request_button')}
      </ApproveButton>

      <Paragraph>{t('chatbot_access_request_paragraph_2')}</Paragraph>
      <Paragraph>
        {t('chatbot_access_request_disclaimer', {
          pp: (chunks: ReactNode) => (
            <a href={privacyPolicyURL(selectedLocale)}>{chunks}</a>
          ),
          pt: (chunks: ReactNode) => (
            <a href="mailto:privacyteam@ouraring.com">{chunks}</a>
          ),
        })}
      </Paragraph>
    </Container>
  );
}
